import { BrowserRouter } from 'react-router'
import { Box, ThemeProvider, createTheme, CssBaseline } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '../../lib/queryClient'
import { ToastContainer } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import 'react-toastify/dist/ReactToastify.css'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import Routes from '../router/Routes'
import { themeColors } from './theme'
import { authStore } from '../../lib/stores/authStore'
import { OrgSelectorProvider } from '../../context/OrgSelectorContext'
import { callStore } from '../../lib/stores/callStore'
import { CallingUI } from '../../components/calls/CallingUI'
import { CallOutcomeModal } from '../../components/calls/CallOutcomeModal'
import { BulkImportLeadsDrawer } from '../../components/drawers/BulkImportLeadsDrawer'
import { signalRService } from '../../lib/services/signalRService'
import { MissedCallContainer } from '../../components/common/MissedCallContainer'


const theme = createTheme({
  palette: {
    primary: { main: themeColors.primary, dark: themeColors.primaryDark, light: themeColors.primaryLight },
    secondary: { main: themeColors.primaryDark },
    error: { main: themeColors.error },
    warning: { main: themeColors.warning },
    success: { main: themeColors.success },
    background: { default: themeColors.background, paper: themeColors.surface },
    text: { primary: themeColors.text.primary, secondary: themeColors.text.secondary },
  },
  typography: {
    fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h5: { fontWeight: 700, fontSize: '20px', letterSpacing: '-0.3px' },
    h6: { fontWeight: 600, fontSize: '16px' },
    body1: { fontSize: '14px' },
    body2: { fontSize: '13px' },
  },
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    themeColors.shadow.sm,
    themeColors.shadow.sm,
    themeColors.shadow.md,
    themeColors.shadow.md,
    themeColors.shadow.md,
    themeColors.shadow.md,
    themeColors.shadow.md,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.lg,
    themeColors.shadow.dialog,
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '6px',
          fontSize: '13px',
          letterSpacing: '0.1px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
        },
        outlined: {
          borderWidth: '1px',
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            fontSize: '13.5px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '13.5px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          boxShadow: themeColors.shadow.lg,
          border: `1px solid ${themeColors.border}`,
        },
        option: {
          fontSize: '13.5px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          boxShadow: themeColors.shadow.dialog,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 12px',
          fontSize: '17px',
          fontWeight: 700,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px 16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: '8px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
          fontSize: '12px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          '& .MuiTabs-indicator': {
            height: 2,
            borderRadius: '2px 2px 0 0',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '13.5px',
          minHeight: 40,
          padding: '6px 16px',
          letterSpacing: '0.1px',
          '&.Mui-selected': { fontWeight: 600 },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '10px',
          boxShadow: themeColors.shadow.lg,
          border: `1px solid ${themeColors.border}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '13.5px',
          borderRadius: '6px',
          mx: '4px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1a1a2e',
          fontSize: '12px',
          borderRadius: '6px',
          padding: '5px 10px',
        },
        arrow: { color: '#1a1a2e' },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '13px',
        },
      },
    },
  },
})

const GlobalCallingOverlay = observer(() => {
  if (callStore.phase === 'IDLE') return null

  return (
    <>
      {callStore.phase !== 'OUTCOME' && <CallingUI />}
      {callStore.phase === 'OUTCOME' && callStore.activeCall && (
        <CallOutcomeModal
          call={callStore.activeCall}
          onSave={(data) => callStore.saveCallOutcome(data)}
          isSaving={callStore.isSavingOutcome}
          statusLockedByWebhook={callStore.statusLockedByWebhook}
        />
      )}
    </>
  )
})

// Inner layout — conditionally shows sidebar/topbar based on auth state
const AppLayout = observer(() => {
  const showShell = authStore.isAuthenticated

  // Populate org/lob/warehouse names whenever the user logs in
  useEffect(() => {
    if (authStore.isAuthenticated) {
      authStore.loadProfile()
      authStore.loadOrgContext()
    }
  }, [authStore.isAuthenticated])

  // Connect SignalR when authenticated; disconnect on logout.
  // Re-runs whenever isAuthenticated toggles so a failed first attempt is retried on re-auth.
  useEffect(() => {
    if (authStore.isAuthenticated) {
      signalRService.connect().catch(() => {})
    } else {
      signalRService.disconnect()
    }
  }, [authStore.isAuthenticated])

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {showShell && <Sidebar />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {showShell && <Topbar />}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: showShell ? themeColors.background : 'transparent',
          }}
        >
          <Routes />
        </Box>
      </Box>
      <GlobalCallingOverlay />
      {showShell && <MissedCallContainer />}
      {showShell && <BulkImportLeadsDrawer />}
    </Box>
  )
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <OrgSelectorProvider>
            <AppLayout />
          </OrgSelectorProvider>
        </BrowserRouter>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          style={{ zIndex: 9999 }}
          toastStyle={{
            borderRadius: '10px',
            fontSize: '13.5px',
            boxShadow: themeColors.shadow.lg,
          }}
        />
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
